import React from "react";
import { InlineStatusLegend } from "./InlineStatusLegend.jsx";
import { ModuleCardRow } from "./ModuleCardRow.jsx";
import { SummaryTile } from "./SummaryTile.jsx";
import { EmptyState } from "./EmptyState.jsx";

/**
 * Canonical ModuleCard per module-card-contract.
 * Compatible with PokerPUR legacy props (moduleId, headerAction, hideHeader, etc.).
 * @param {object} props
 */
export function ModuleCard({
  moduleId,
  title,
  headerAction,
  children,
  className = "",
  bodyClassName = "",
  testId,
  ariaLabel,
  hideHeader = false,
  headerTone = "golfmap-green",
  bodyTone = "soft-blue",
  inlineStatusLegend = null,
  rows = [],
  summaryMetrics = null,
  emptyState = null,
  mobileBehavior = null,
  onRowClick,
}) {
  const maxRows =
    mobileBehavior?.maxRowsOnHome && typeof window !== "undefined" && window.innerWidth < 768
      ? mobileBehavior.maxRowsOnHome
      : rows.length;
  const visibleRows = rows.slice(0, maxRows || rows.length);

  const bodyToneClass =
    bodyTone === "connected-surface-soft"
      ? "cq-module-card__body--connected-surface-soft"
      : "cq-module-card__body--soft-blue";

  const renderHeaderAction = () => {
    if (!headerAction) return null;
    if (React.isValidElement(headerAction)) {
      return <div className="cq-module-card__head-action">{headerAction}</div>;
    }
    if (typeof headerAction === "object" && headerAction.label) {
      const action = headerAction;
      if (action.href) {
        return (
          <div className="cq-module-card__head-action">
            <a className="cq-module-card__head-action-link" href={action.href}>
              {action.label}
            </a>
          </div>
        );
      }
      return (
        <div className="cq-module-card__head-action">
          <button type="button" className="cq-module-card__head-action-link" onClick={action.onClick}>
            {action.label}
          </button>
        </div>
      );
    }
    return null;
  };

  const hasStructuredContent =
    inlineStatusLegend || summaryMetrics?.length || visibleRows.length || emptyState;

  return (
    <section
      className={`cq-module-card ${className}`.trim()}
      data-module-id={moduleId}
      data-testid={testId || (moduleId ? `module-card-${moduleId}` : undefined)}
      aria-label={ariaLabel || title}
    >
      {hideHeader ? null : (
        <header className={`cq-module-card__head cq-module-card__head--${headerTone}`}>
          <h3 className="cq-module-card__title">{title}</h3>
          {renderHeaderAction()}
        </header>
      )}
      <div className={`cq-module-card__body ${bodyToneClass} ${bodyClassName}`.trim()}>
        {inlineStatusLegend ? <InlineStatusLegend {...inlineStatusLegend} /> : null}
        {summaryMetrics?.length ? (
          <div className="cq-module-card__summary" style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
            {summaryMetrics.map((metric) => (
              <SummaryTile
                key={metric.key}
                label={metric.label}
                value={metric.value}
                tone={metric.tone}
                testId={`summary-${metric.key}`}
              />
            ))}
          </div>
        ) : null}
        {visibleRows.length ? (
          <div className="cq-module-card__rows">
            {visibleRows.map((row) => (
              <ModuleCardRow key={row.rowId} row={row} onRowClick={onRowClick} />
            ))}
          </div>
        ) : null}
        {!visibleRows.length && emptyState ? (
          <EmptyState
            message={emptyState.message}
            ctaLabel={emptyState.ctaLabel}
            onCtaClick={emptyState.onCtaClick}
          />
        ) : null}
        {!hasStructuredContent ? children : children}
      </div>
    </section>
  );
}
